/*
 * 加载shp 数据 并生成 ol layer
 * @Author: Alex
 * @Date: 2023-03-17 13:39:09
 * @LastEditors: Alex yxfacw@163.com
 * @LastEditTime: 2023-05-23 09:58:20
 * @Description:
 */
import { ShapeFeatureReader, ShapeFeatureCollection } from 'ts-shapefile'
import { Vector as VectorLayer } from 'ol/layer'
import { Vector } from 'ol/source'
import { MapLayerType, VectorFeatureStyleType } from '../mapSpecification'
import { Fill, Stroke, Style, Icon } from 'ol/style'
import { getBottomLeft, getHeight, getWidth } from 'ol/extent'
import { toContext } from 'ol/render'
import { Point, Polygon } from 'ol/geom'
import { GeoJSON } from 'ol/format'
import { Coordinate } from 'ol/coordinate'
import { MapLayerLoader, LayerTypes } from './mapLayerLoader'
import { mapContext } from '../mapContext'
import { info, warn } from '../console'

export interface ShapfileLayerOptions {
  url: string
  // shp 数据的空间参考
  crs: string
  // 指定属性数据编码 默认：UTF-8
  encoding?: string
}

export class ShapfileLayer {
  readonly id: string
  readonly url: string
  readonly type: string = LayerTypes.Shp
  readonly options: ShapfileLayerOptions &
    (MapLayerType | { source?: any; [_: string]: any })
  #features: ShapeFeatureCollection | undefined = undefined

  constructor(
    id: string,
    options: ShapfileLayerOptions &
      (MapLayerType | { source?: any; id?: string; [_: string]: any })
  ) {
    this.id = id
    this.options = options
    this.url = this.options.url
  }

  /**
   * convert to ol layer
   * @returns ol map layer
   */
  async toMapLayer(): Promise<any> {
    // 解析
    if (this.#features == undefined) {
      this.#features = await this.readShapeFeatures()
    }
    // 生成 VectorLayer TODO: crs 转换
    const geojson = this.#features?.toGeoJson()
    info('geojson: ', geojson)
    const source = new Vector()
    const styleType = this.options.style as VectorFeatureStyleType
    if (styleType.imageSample) {
      // image 填充样式
      source?.on('addfeature', function (event) {
        const feature = event.feature
        if (feature) {
          const properties = feature.getProperties()
          const colorSample = styleType.imageSample ?? []
          // FIXME: 这里 `fill` 需要优化
          const propVal = properties[styleType.fill?.propertyName ?? '']
          if (propVal !== undefined) {
            const hitted = colorSample.find(cs => cs.value == propVal)
            if (hitted) {
              const img = new Image()
              img.onload = function () {
                feature.set('styleImg', img)
              }
              img.src = hitted.imgUrl
            }
          }
        }
      })
    }
    source.addFeatures(
      new GeoJSON().readFeatures(geojson, {
        dataProjection: this.options.crs,
        featureProjection: mapContext.getMapStyle().crs
      })
    )
    return new VectorLayer({
      ...this.options,
      ...{
        id: this.id,
        source: source,
        style: styleType?.imageSample
          ? new Style({
              renderer: function (pixelCoordinates, state) {
                const context = state.context
                const styleImg = state.feature.get(
                  'styleImg'
                ) as HTMLImageElement
                if (!styleImg) {
                  return
                }
                const geoType = state.geometry.clone().getType()
                switch (geoType) {
                  case 'Polygon':
                    {
                      const geometry = state.geometry.clone() as Polygon
                      geometry.setCoordinates(
                        pixelCoordinates as Coordinate[][]
                      )
                      const extent = geometry.getExtent()
                      const width = getWidth(extent)
                      const height = getHeight(extent)

                      if (height < 1 || width < 1) {
                        return
                      }
                      context.save()
                      const renderContext = toContext(context, {
                        pixelRatio: 1
                      })
                      renderContext.setFillStrokeStyle(
                        new Fill(),
                        new Stroke({
                          color: 'rgba(255,255,255,0.8)',
                          width: 2
                        })
                      )
                      renderContext.drawGeometry(geometry)
                      context.clip()

                      const bottomLeft = getBottomLeft(extent)
                      const left = bottomLeft[0]
                      const bottom = bottomLeft[1]
                      context.drawImage(styleImg, left, bottom, width, height)
                      context.restore()
                    }
                    break
                  case 'Point':
                    {
                      const point = state.geometry.clone() as Point
                      point.setCoordinates(pixelCoordinates as Coordinate[][])
                      context.save()
                      const renderContext = toContext(context, {
                        pixelRatio: 1
                      })
                      renderContext.setImageStyle(
                        new Icon({
                          img: styleImg,
                          imgSize: [styleImg.width, styleImg.height]
                        })
                      )
                      renderContext.drawGeometry(point)
                      context.clip()
                      context.restore()
                    }
                    break
                }
              }
            })
          : feature =>
              MapLayerLoader.getVectorStyle(
                feature,
                (this.options.style as VectorFeatureStyleType) ?? undefined
              )
      }
    })
  }

  private async readShapeFeatures(): Promise<
    ShapeFeatureCollection | undefined
  > {
    if (this.url.length > 0) {
      // get name
      const shpName = this.url
        .substring(this.url.lastIndexOf('/') + 1)
        .replace('.shp', '')

      const shpPromise = fetch(this.url).then(res => res.arrayBuffer())
      const shxPromise = fetch(this.url.replace('.shp', '.shx')).then(res =>
        res.arrayBuffer()
      )
      const dbfPromise = fetch(this.url.replace('.shp', '.dbf')).then(res =>
        res.arrayBuffer()
      )
      // cpg若不存在 则使用配置项编码值生成
      let cpgFile: File | undefined = undefined
      const cpgPromise = fetch(this.url.replace('.shp', '.cpg'))
        .then(res => {
          if (res.ok) {
            return res.arrayBuffer()
          } else {
            throw new Error(
              '[shp-layer] get .cpg error: '.concat(res.statusText)
            )
          }
        })
        .then(res => {
          cpgFile = new File([res], shpName.concat('.cpg'))
        })
        .catch(e => {
          warn(e)
          cpgFile = new File(
            [this.str2ab(this.options.encoding ?? 'UTF-8')],
            shpName.concat('.cpg')
          )
        })
      const values = await Promise.all([
        shpPromise,
        shxPromise,
        dbfPromise,
        cpgPromise
      ])
      const reader = await ShapeFeatureReader.fromFiles(
        new File([values[0] as ArrayBuffer], shpName.concat('.shp')),
        new File([values[1] as ArrayBuffer], shpName.concat('.shx')),
        new File([values[2] as ArrayBuffer], shpName.concat('.dbf')),
        cpgFile
      )
      const collection = await reader.readFeatureCollection()
      return collection
    }

    return undefined
  }

  private str2ab(str: string): ArrayBuffer {
    const buf = new ArrayBuffer(str.length * 2) // 每个字符占用2个字节
    const bufView = new Uint16Array(buf)
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i)
    }
    return buf
  }
}
